import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationsAPI } from '../lib/api';
import { Button } from './ui/button';
import { CheckCircle, AlertTriangle, XCircle, Info, Shield, X } from 'lucide-react';

const TYPE_CONFIG = {
  success: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', label: 'Успех' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', label: 'Предупреждение' },
  error: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', label: 'Ошибка' },
  info: { icon: Info, color: 'text-sky-400', bg: 'bg-sky-500/15', border: 'border-sky-500/30', label: 'Информация' },
  security: { icon: Shield, color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30', label: 'Безопасность' },
};

const PRIORITY_BADGE = {
  high: { label: 'Важно', cls: 'bg-red-500/20 text-red-400 border-red-500/30' },
  medium: { label: 'Обычный', cls: 'bg-sky-500/20 text-sky-400 border-sky-500/30' },
  low: { label: 'Низкий', cls: 'bg-muted text-muted-foreground border-border/50' },
};

export default function NotificationPopup() {
  const [popups, setPopups] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const fetchPopups = useCallback(async () => {
    try {
      const { data } = await notificationsAPI.list();
      const undismissed = (data.popup_notifications || []);
      setPopups(undismissed);
    } catch {} finally { setLoaded(true); }
  }, []);

  useEffect(() => { fetchPopups(); }, [fetchPopups]);

  const handleDismiss = async (id) => {
    try { await notificationsAPI.dismiss(id); } catch {}
    if (currentIndex < popups.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setPopups([]);
      setCurrentIndex(0);
    }
  };

  const handleDismissAll = async () => {
    try { await notificationsAPI.dismissAll(); } catch {}
    setPopups([]);
    setCurrentIndex(0);
  };

  if (!loaded || popups.length === 0) return null;
  const notif = popups[currentIndex];
  if (!notif) return null;
  const config = TYPE_CONFIG[notif.type] || TYPE_CONFIG.info;
  const Icon = config.icon;
  const priorityInfo = PRIORITY_BADGE[notif.priority] || PRIORITY_BADGE.medium;

  return (
    <AnimatePresence>
      <motion.div
        key="notification-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
        data-testid="notification-popup-overlay"
      >
        <motion.div
          key={notif.id}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-md max-h-[85vh] rounded-2xl border border-border/50 bg-card shadow-2xl overflow-hidden flex flex-col"
          data-testid="notification-popup-card"
        >
          {/* Top accent line */}
          <div className={`h-1 w-full ${config.bg.replace('/15', '')} flex-shrink-0`} />

          {/* Header (sticky top) */}
          <div className="px-6 pt-5 pb-3 flex items-start justify-between gap-3 flex-shrink-0 border-b border-border/30">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-xl ${config.bg} border ${config.border} flex items-center justify-center flex-shrink-0`}>
                <Icon size={20} className={config.color} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${config.color}`}>{config.label}</span>
                  {notif.priority === 'high' && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium ${priorityInfo.cls}`}>{priorityInfo.label}</span>
                  )}
                </div>
                <h3 className="text-base font-bold mt-0.5 break-words">{notif.title}</h3>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {popups.length > 1 && (
                <span className="text-[10px] text-muted-foreground font-medium bg-secondary/50 px-2 py-1 rounded-lg">
                  {currentIndex + 1} / {popups.length}
                </span>
              )}
              <button
                onClick={() => handleDismiss(notif.id)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"
                data-testid="notification-popup-close-btn"
                aria-label="Закрыть"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body (scrollable) */}
          <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0" data-testid="notification-popup-body">
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line break-words">{notif.message}</p>
          </div>

          {/* Footer (sticky bottom) */}
          <div className="px-6 py-4 flex items-center justify-between gap-3 flex-shrink-0 border-t border-border/30 bg-card">
            {popups.length > 1 ? (
              <button
                onClick={handleDismissAll}
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                data-testid="dismiss-all-btn"
              >
                Закрыть все ({popups.length})
              </button>
            ) : <div />}
            <Button
              onClick={() => handleDismiss(notif.id)}
              className="px-6 h-10 font-semibold"
              data-testid="dismiss-notification-btn"
            >
              Понятно
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// For use in the notification dropdown - type icon helper
export function NotifTypeIcon({ type, size = 14 }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.info;
  const Icon = config.icon;
  return <Icon size={size} className={config.color} />;
}

export { TYPE_CONFIG };
